/**
 * TrainerSessionDetail — Vague 99.5
 *
 * Mobile-first detail page that aggregates EVERYTHING a freelance needs to
 * deliver a session for the OF: dates, location, learners (filtered per ACL),
 * full programme, related company, contact pédagogique, resources, and quick
 * actions (open émargement / launch convocation).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Calendar, MapPin, Clock, Users as UsersIcon, FileText, Mail, Phone,
  Building2, ClipboardCheck, ExternalLink, Loader2, Briefcase, AlertCircle,
  MessageSquare,
} from 'lucide-react';
import api from '../../api/client';

export default function TrainerSessionDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/me/trainer/sessions/${sessionId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || 'Session introuvable'));
  }, [sessionId]);

  if (error) return (
    <div className="space-y-3" data-testid="trainer-session-detail-error">
      <button onClick={() => navigate(-1)} className="text-[12px] text-[#4F46E5] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Retour</button>
      <div className="soft-card p-6 border-l-4 border-l-[#EF4444]">
        <div className="flex items-center gap-2 mb-1"><AlertCircle className="w-4 h-4 text-[#EF4444]" /> <span className="text-[13px] font-semibold text-[#1A1B2E]">{error}</span></div>
      </div>
    </div>
  );

  if (!data) return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" /></div>;

  const periods = Array.isArray(data.date_ranges) && data.date_ranges.length > 0
    ? data.date_ranges
    : (data.startDate ? [{ startDate: data.startDate, endDate: data.endDate || data.startDate }] : []);

  return (
    <div className="space-y-3" data-testid={`trainer-session-detail-${data.id}`}>
      <button onClick={() => navigate(-1)} className="text-[12px] text-[#4F46E5] inline-flex items-center gap-1" data-testid="trainer-session-back"><ArrowLeft className="w-3 h-3" /> Retour</button>

      {/* Hero */}
      <div className="soft-card p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-1">{data.status || 'Session'}</div>
            <h1 className="text-[20px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.title || data.program?.name || 'Session'}</h1>
            {data.client && <div className="text-[13px] text-[#6B6C85] mt-1">{data.client}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] text-[#8E8FA5]">Apprenants</div>
            <div className="text-[24px] font-bold text-[#4F46E5] leading-none">{data.learners_total || 0}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[12.5px] text-[#4A4B63]">
          <div className="flex items-start gap-2 bg-[#FAFAFE] rounded-lg px-3 py-2">
            <Calendar className="w-3.5 h-3.5 mt-0.5 text-[#4F46E5]" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase font-bold text-[#8E8FA5] tracking-wide">Périodes</div>
              {periods.length === 0 ? <div>—</div> : periods.map((p, i) => (
                <div key={i}>{p.startDate}{p.endDate && p.endDate !== p.startDate ? ` → ${p.endDate}` : ''}</div>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-2 bg-[#FAFAFE] rounded-lg px-3 py-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#4F46E5]" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase font-bold text-[#8E8FA5] tracking-wide">Lieu</div>
              <div className="truncate">{data.location || data.modality || '—'}</div>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-[#FAFAFE] rounded-lg px-3 py-2">
            <Clock className="w-3.5 h-3.5 mt-0.5 text-[#4F46E5]" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase font-bold text-[#8E8FA5] tracking-wide">Durée</div>
              <div>{data.duration ? `${data.duration} h` : '—'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Program */}
      {data.program && (
        <div className="soft-card p-5" data-testid="trainer-session-program">
          <div className="flex items-center gap-2 mb-2"><Briefcase className="w-4 h-4 text-[#4F46E5]" /> <h2 className="text-[14px] font-bold text-[#1A1B2E]">Programme</h2></div>
          <div className="text-[13px] font-semibold text-[#1A1B2E] mb-1.5">{data.program.name}</div>
          {data.program.synopsis && <p className="text-[12.5px] text-[#4A4B63] leading-relaxed mb-2 whitespace-pre-line">{data.program.synopsis}</p>}
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            {data.program.objectives && (
              <Block label="Objectifs" value={data.program.objectives} />
            )}
            {data.program.prerequisites && (
              <Block label="Prérequis" value={data.program.prerequisites} />
            )}
            {data.program.duration_h && <Block label="Durée prog." value={`${data.program.duration_h} h`} />}
            {data.program.modality && <Block label="Modalité" value={data.program.modality} />}
            {data.program.category && <Block label="Catégorie" value={data.program.category} />}
          </div>
        </div>
      )}

      {/* Company */}
      {data.company && (
        <div className="soft-card p-5" data-testid="trainer-session-company">
          <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-[#4F46E5]" /> <h2 className="text-[14px] font-bold text-[#1A1B2E]">Entreprise cliente</h2></div>
          <div className="text-[13px] font-semibold text-[#1A1B2E]">{data.company.name}</div>
          <div className="text-[12px] text-[#6B6C85] mt-0.5">
            {[data.company.address, data.company.postal_code, data.company.city].filter(Boolean).join(', ')}
          </div>
          {data.company.siret && <div className="text-[11.5px] text-[#8E8FA5] mt-0.5 font-mono">SIRET : {data.company.siret}</div>}
        </div>
      )}

      {/* Pedagogical contact */}
      {data.pedagogical_contact && (
        <div className="soft-card p-5" data-testid="trainer-session-contact">
          <div className="flex items-center gap-2 mb-2"><Mail className="w-4 h-4 text-[#4F46E5]" /> <h2 className="text-[14px] font-bold text-[#1A1B2E]">Contact pédagogique</h2></div>
          <div className="text-[13px] font-semibold text-[#1A1B2E]">{data.pedagogical_contact.name}</div>
          {data.pedagogical_contact.job_title && <div className="text-[12px] text-[#6B6C85]">{data.pedagogical_contact.job_title}</div>}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-[12px]">
            {data.pedagogical_contact.email && (
              <a href={`mailto:${data.pedagogical_contact.email}`} className="text-[#4F46E5] inline-flex items-center gap-1 hover:underline" data-testid="trainer-contact-email"><Mail className="w-3 h-3" /> {data.pedagogical_contact.email}</a>
            )}
            {data.pedagogical_contact.phone && (
              <a href={`tel:${data.pedagogical_contact.phone}`} className="text-[#4F46E5] inline-flex items-center gap-1 hover:underline" data-testid="trainer-contact-phone"><Phone className="w-3 h-3" /> {data.pedagogical_contact.phone}</a>
            )}
          </div>
        </div>
      )}

      {/* Learners */}
      <div className="soft-card p-5" data-testid="trainer-session-learners">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2"><UsersIcon className="w-4 h-4 text-[#4F46E5]" /> <h2 className="text-[14px] font-bold text-[#1A1B2E]">Apprenants ({data.learners_total || 0})</h2></div>
          {data.learners_contact_mode === 'masked' && <span className="text-[10.5px] text-[#92400E] bg-[#FEF3C7] px-2 py-0.5 rounded font-semibold">Coordonnées masquées</span>}
          {data.learners_contact_mode === 'hidden' && <span className="text-[10.5px] text-[#6B6C85] bg-[#F3F4F8] px-2 py-0.5 rounded font-semibold">Coordonnées masquées</span>}
        </div>
        {(!data.learners || data.learners.length === 0) ? (
          <div className="text-[12.5px] italic text-[#8E8FA5]">Aucun apprenant inscrit pour le moment.</div>
        ) : (
          <ul className="divide-y divide-[#F3F4F8]">
            {data.learners.map((l, i) => (
              <li key={l.id || i} className="py-2 flex items-start justify-between gap-3" data-testid={`trainer-learner-${i}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{l.name || '—'}</div>
                  {l.company && <div className="text-[11.5px] text-[#8E8FA5] truncate">{l.company}</div>}
                  {(l.email || l.phone) && (
                    <div className="text-[11.5px] text-[#6B6C85] mt-0.5 flex flex-wrap gap-x-3">
                      {l.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>}
                      {l.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>}
                    </div>
                  )}
                </div>
                {typeof l.progress === 'number' && (
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-[#8E8FA5]">Progression</div>
                    <div className="text-[13px] font-bold text-[#4F46E5]">{l.progress}%</div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Resources */}
      {data.resources && data.resources.length > 0 && (
        <div className="soft-card p-5" data-testid="trainer-session-resources">
          <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#4F46E5]" /> <h2 className="text-[14px] font-bold text-[#1A1B2E]">Ressources pédagogiques</h2></div>
          <ul className="space-y-1.5">
            {data.resources.map((r, i) => (
              <li key={i} className="flex items-center gap-2 text-[12.5px] text-[#4A4B63]">
                <FileText className="w-3.5 h-3.5 text-[#4F46E5]" />
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-[#4F46E5] hover:underline inline-flex items-center gap-1">{r.label} <ExternalLink className="w-3 h-3" /></a>
                ) : (
                  <span>{r.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <Link
          to={`/app/emargements?sessionId=${encodeURIComponent(data.id)}`}
          className="h-12 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center justify-center gap-2"
          data-testid="trainer-session-action-emargement"
        >
          <ClipboardCheck className="w-4 h-4" /> Émarger
        </Link>
        <Link
          to={`/app/sessions?focus=${encodeURIComponent(data.id)}`}
          className="h-12 rounded-xl bg-white border border-[#EFF0F6] text-[#4A4B63] text-[13px] font-semibold flex items-center justify-center gap-2"
          data-testid="trainer-session-action-detail"
        >
          <ExternalLink className="w-4 h-4" /> Voir dans l'app
        </Link>
      </div>

      <ReponsesDeMaSession sessionId={data.id} />
    </div>
  );
}

// Ce que les apprenants ont répondu : le positionnement avant, la validation
// des acquis après. Pas la satisfaction — elle porte sur le formateur, et ce
// n'est pas à lui qu'elle est adressée.
function ReponsesDeMaSession({ sessionId }) {
  const [data, setData] = useState(null);
  const [ouverte, setOuverte] = useState(null);

  useEffect(() => {
    api.get(`/me/trainer/sessions/${sessionId}/questionnaires`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ reponses: [], questionnaires: [], envoyees: 0, recues: 0 }));
  }, [sessionId]);

  if (!data || (!data.recues && !data.envoyees)) return null;

  const definition = (qid) => (data.questionnaires || []).find((q) => q.id === qid);
  const libelle = (qid, questionId) => {
    const q = definition(qid);
    return (q?.questions || []).find((x) => x.id === questionId)?.label || questionId;
  };

  return (
    <div className="soft-card p-4" data-testid="trainer-reponses">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold text-[#1A1B2E] flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4 text-[#4F46E5]" /> Ce que vos apprenants ont répondu
        </div>
        <span className="text-[11px] text-[#8E8FA5]">
          {data.recues} / {data.envoyees} reçue(s)
        </span>
      </div>

      {data.recues === 0 ? (
        <div className="text-[12px] text-[#8E8FA5]">
          Aucune réponse pour l&rsquo;instant.
        </div>
      ) : (
        <div className="space-y-1.5">
          {data.reponses.map((r) => {
            const ouvert = ouverte === r.id;
            const q = definition(r.questionnaire_id);
            const note = r.correction?.pourcentage;
            return (
              <div key={r.id} className="rounded-lg border border-[#EFF0F6] bg-white">
                <button
                  onClick={() => setOuverte(ouvert ? null : r.id)}
                  data-testid={`trainer-reponse-${r.id}`}
                  className="w-full text-left px-3 py-2 flex items-center gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">
                      {r.learner_name}
                    </div>
                    <div className="text-[10.5px] text-[#8E8FA5] truncate">
                      {q?.title || r.kind}
                    </div>
                  </div>
                  {note !== undefined && note !== null && (
                    <span className={`shrink-0 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${
                      r.correction?.acquis === false
                        ? 'bg-[#FEF6F6] text-[#B91C1C]'
                        : 'bg-[#E6FBF7] text-[#0F766E]'
                    }`}>
                      {note} %
                    </span>
                  )}
                </button>
                {ouvert && (
                  <div className="px-3 pb-2.5 space-y-1.5 border-t border-[#F6F7FB] pt-2">
                    {Object.entries(r.answers || {}).map(([qid, valeur]) => (
                      <div key={qid}>
                        <div className="text-[10.5px] font-semibold text-[#6B6C85]">
                          {libelle(r.questionnaire_id, qid)}
                        </div>
                        <div className="text-[12px] text-[#1A1B2E] whitespace-pre-line">
                          {Array.isArray(valeur) ? valeur.join(' · ') : String(valeur ?? '—')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Block({ label, value }) {
  return (
    <div className="bg-[#FAFAFE] rounded-lg p-2.5">
      <div className="text-[10.5px] font-bold uppercase tracking-wide text-[#8E8FA5] mb-0.5">{label}</div>
      <div className="text-[12.5px] text-[#1A1B2E] whitespace-pre-line">{value}</div>
    </div>
  );
}
